/**
 * @fileoverview Client for interacting with the invoices service.
 *
 * Extends GraphQLClientBase for consistent retry logic and error handling.
 */
import { ApplicationError } from "@eoln/errors";
import { GraphQLClientBase, type GraphQLClientOptions } from "@eoln/graphql-client";
import type {
  CreateInvoiceInput,
  CreateRegistryInput,
  CreateSequenceInput,
  Invoice,
  InvoiceListResponse,
  InvoiceResponse,
  Registry,
  RegistryResponse,
  RegistryWithFullSequence,
  Sequence,
  SequenceHistoryResponse,
  SequenceResponse,
  UpdateInvoiceInput,
  UpdateRegistryInput,
  UpdateSequenceInput,
} from "../../../srv/invoices/__generated__/types.ts";

/**
 * Configuration options for the InvoicesClient.
 */
export interface InvoicesClientOptions extends GraphQLClientOptions {}

/**
 * Client for interacting with the invoices service.
 *
 * This class provides methods for:
 * - Invoice management (creating, retrieving, listing)
 * - Registry management
 * - Sequence management
 *
 * Features:
 * - Retry with exponential backoff for transient failures (via @eoln/resilience)
 * - Consistent error handling (ErrorAuthentication, ErrorAuthorization)
 * - Health metrics for monitoring retry behavior
 *
 * @example
 * ```typescript
 * const client = new InvoicesClient({
 *   endpoint: 'https://invoices.api.example.com/graphql',
 *   apiKey: 'sk_...',
 * });
 *
 * // Create an invoice - returns unwrapped Invoice object directly
 * // (GraphQL response { createInvoice: { invoice: {...} } } is handled internally)
 * const invoice = await client.createInvoice(invoiceData);
 * console.log(invoice.invoiceId);     // UUID
 * console.log(invoice.serialNumber);  // e.g., "FV/2025/001"
 * console.log(invoice.total.amount);  // Total amount in grosze
 *
 * // Get invoice - returns Invoice | null
 * const fetched = await client.getInvoice(invoice.invoiceId, invoice.registryId);
 *
 * // List invoices - returns { invoices: Invoice[], total: number, hasMore: boolean }
 * const list = await client.listInvoices({
 *   filter: { registryId: 'uuid', paid: false },
 *   pagination: { limit: 20, offset: 0 },
 * });
 *
 * // Check health metrics for monitoring retry behavior
 * console.log(client.getHealthMetrics());
 * // { totalRequests: 5, successfulRequests: 5, failedRequests: 0, retryCount: 0 }
 * ```
 */
export class InvoicesClient extends GraphQLClientBase {
  /**
   * Creates a new InvoicesClient instance.
   * @param options - Configuration options
   */
  constructor(options: InvoicesClientOptions) {
    if (!options.endpoint) {
      throw new Error("[InvoicesClient Error]: Endpoint URL is required.");
    }
    super(options);
  }

  /**
   * Converts Date objects to YYYY-MM-DD strings for GraphQL Date scalar.
   * @param date - Date object or string
   * @returns Date string in YYYY-MM-DD format
   */
  private formatDateForGraphQL(date: Date | string): string {
    if (typeof date === "string") {
      return date;
    }
    return date.toISOString().split("T")[0] ?? "";
  }

  /**
   * Prepares invoice input by converting Date objects to strings.
   * @param input - The invoice input with Date objects
   * @returns Invoice input with dates as strings
   */
  private prepareInvoiceInput(input: CreateInvoiceInput): Record<string, unknown> {
    return {
      ...input,
      issued: this.formatDateForGraphQL(input.issued),
      due: this.formatDateForGraphQL(input.due),
      sold: this.formatDateForGraphQL(input.sold),
      paid: input.paid ? this.formatDateForGraphQL(input.paid) : undefined,
    };
  }

  /**
   * Prepares update invoice input by converting any provided Date objects to strings.
   * Unlike {@link prepareInvoiceInput}, all date fields are optional here since
   * updateInvoice only patches the fields the caller supplies.
   * @param input - The update invoice input, possibly containing Date objects
   * @returns Update invoice input with any provided dates as strings
   */
  private prepareUpdateInvoiceInput(input: UpdateInvoiceInput): Record<string, unknown> {
    return {
      ...input,
      issued: input.issued ? this.formatDateForGraphQL(input.issued) : undefined,
      due: input.due ? this.formatDateForGraphQL(input.due) : undefined,
      sold: input.sold ? this.formatDateForGraphQL(input.sold) : undefined,
      paid: input.paid ? this.formatDateForGraphQL(input.paid) : undefined,
    };
  }

  /**
   * Normalizes draft content dates to YYYY-MM-DD format.
   * Handles Date objects, ISO datetime strings, and date-only strings.
   * GraphQL Date scalar requires strict YYYY-MM-DD format.
   * @param content - The draft content object
   * @returns Content with normalized date fields
   */
  private normalizeDraftContentDates(content: Record<string, unknown>): Record<string, unknown> {
    const dateFields = ["issued", "sold", "due", "paid"];
    const result = { ...content };

    dateFields.forEach((field) => {
      const value = result[field];
      if (value === null || value === undefined) {
        return;
      }
      if (value instanceof Date) {
        result[field] = this.formatDateForGraphQL(value);
      } else if (typeof value === "string") {
        // Extract date portion from any string that might contain time components
        // Handles: "2025-12-16T00:00:00.000Z", "2025-12-16 00:00:00", "2025-12-16"
        const dateOnly = value.split(/[T\s]/)[0];
        // Validate it looks like a date (YYYY-MM-DD)
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
          result[field] = dateOnly;
        }
        // If doesn't match date pattern, leave as-is and let server validate
      }
    });

    return result;
  }

  /**
   * Creates a new invoice.
   * @param input - The invoice data.
   * @returns The created invoice.
   * @throws {ErrorAuthentication} If API key is required but not provided.
   * @throws {ApplicationError} If the GraphQL request fails.
   */
  async createInvoice(input: CreateInvoiceInput): Promise<Invoice> {
    const query = `
      mutation CreateInvoice($input: CreateInvoiceInput!) {
        createInvoice(input: $input) {
          invoice {
             invoiceId
             registryId
             serialNumber
             seller {
               name
               taxId
               address { street city zip country countryCode }
               iban
             }
             buyer {
               name
               taxId
               address { street city zip country countryCode }
             }
             items {
               name
               description
               quantity
               measureUnit
               unitPrice { rate netto brutto tax }
               totalPrice { rate netto brutto tax }
               taxCode
               reference
             }
             total { amount currency }
             groupedTax { rate netto brutto tax }
             createdAt
             modifiedAt
             due
             issued
             sold
             paid
          }
          versionstamp
        }
      }
    `;
    // Convert Date objects to strings for GraphQL
    const preparedInput = this.prepareInvoiceInput(input);
    const variables = { input: preparedInput };
    const response = await this.graphqlRequest<{ createInvoice: InvoiceResponse | null }>(query, variables);
    if (!response?.createInvoice?.invoice) {
      throw new ApplicationError("Invalid response structure from createInvoice mutation", "INVALID_RESPONSE");
    }
    return response.createInvoice.invoice;
  }

  /**
   * Retrieves a specific invoice by its ID.
   * @param invoiceId - The UUID of the invoice to retrieve.
   * @param registryId - The UUID of the registry the invoice belongs to.
   * @returns The requested invoice or null if not found.
   * @throws {ErrorAuthentication} If API key is required but not provided.
   * @throws {ApplicationError} If the GraphQL request fails.
   */
  async getInvoice(invoiceId: string, registryId: string): Promise<Invoice | null> {
    const query = `
      query GetInvoice($invoiceId: UUID!, $registryId: UUID!) {
        getInvoice(invoiceId: $invoiceId, registryId: $registryId) {
           invoice {
             invoiceId
             registryId
             serialNumber
             seller {
               name
               taxId
               address { street city zip country countryCode }
               iban
             }
             buyer {
               name
               taxId
               address { street city zip country countryCode }
             }
             items {
               name
               description
               quantity
               measureUnit
               unitPrice { rate netto brutto tax }
               totalPrice { rate netto brutto tax }
               taxCode
               reference
             }
             total { amount currency }
             groupedTax { rate netto brutto tax }
             createdAt
             modifiedAt
             due
             issued
             sold
             paid
             issueType
             correctionOf {
               invoiceId
               registryId
               serialNumber
             }
           }
        }
      }
    `;
    const variables = { invoiceId, registryId };
    const response = await this.graphqlRequest<{ getInvoice: InvoiceResponse | null }>(query, variables);
    return response?.getInvoice?.invoice ?? null;
  }

  /**
   * Lists invoices with optional filtering and pagination.
   * @param options - Options for filtering and pagination
   * @returns Paginated list of invoices with metadata
   * @throws {ErrorAuthentication} If API key is required but not provided.
   * @throws {ApplicationError} If the GraphQL request fails.
   */
  async listInvoices(
    options: {
      filter?: {
        registryId?: string;
        paid?: boolean;
        startDate?: string;
        endDate?: string;
        issueType?: "PROFORMA" | "VAT";
        isCorrection?: boolean;
      };
      pagination?: {
        limit?: number;
        offset?: number;
      };
    } = {},
  ): Promise<InvoiceListResponse> {
    const query = `
      query ListInvoices($filter: InvoiceFilter, $pagination: InvoicePagination) {
        listInvoices(filter: $filter, pagination: $pagination) {
          invoices {
            invoiceId
            registryId
            serialNumber
            seller {
              name
              taxId
              address { street city zip country countryCode }
              iban
            }
            buyer {
              name
              taxId
              address { street city zip country countryCode }
            }
            items {
              name
              description
              quantity
              measureUnit
              unitPrice { rate netto brutto tax }
              totalPrice { rate netto brutto tax }
              taxCode
              reference
            }
            total { amount currency }
            groupedTax { rate netto brutto tax }
            createdAt
            modifiedAt
            due
            issued
            sold
            paid
            issueType
            correctionOf {
              invoiceId
              registryId
              serialNumber
            }
          }
          total
          hasMore
        }
      }
    `;
    const variables = {
      filter: options.filter || {},
      pagination: options.pagination || { limit: 50, offset: 0 },
    };
    const response = await this.graphqlRequest<{ listInvoices: InvoiceListResponse }>(query, variables);
    return response.listInvoices;
  }

  // --- Registry Methods ---

  /**
   * Creates a new registry.
   * @param input - Data for the new registry.
   * @returns The created registry with full sequence details.
   * @throws {ErrorAuthentication} If API key is required but not provided.
   * @throws {ApplicationError} If the GraphQL request fails.
   */
  async createRegistry(input: CreateRegistryInput): Promise<RegistryWithFullSequence> {
    const query = `
      mutation CreateRegistry($input: CreateRegistryInput!) {
        createRegistry(input: $input) {
          registry {
            registryId
            name
            sequenceId
            createdAt
            modifiedAt
            sequence {
              sequenceId
              name
              start
              template
              counter
              value
              createdAt
              modifiedAt
            }
          }
          versionstamp
        }
      }
    `;
    const variables = { input };
    const response = await this.graphqlRequest<{ createRegistry: RegistryResponse | null }>(query, variables);
    if (!response?.createRegistry?.registry) {
      throw new ApplicationError("Invalid response structure from createRegistry mutation", "INVALID_RESPONSE");
    }
    return response.createRegistry.registry;
  }

  /**
   * Retrieves a specific registry by its ID.
   * @param registryId - The UUID of the registry to retrieve.
   * @returns The requested registry with full sequence details or null if not found.
   * @throws {ErrorAuthentication} If API key is required but not provided.
   * @throws {ApplicationError} If the GraphQL request fails.
   */
  async getRegistry(registryId: string): Promise<RegistryWithFullSequence | null> {
    const query = `
      query GetRegistry($registryId: UUID!) {
        getRegistry(registryId: $registryId) {
           registry {
            registryId
            name
            sequenceId
            createdAt
            modifiedAt
            sequence {
              sequenceId
              name
              start
              template
              counter
              value
              createdAt
              modifiedAt
            }
          }
          versionstamp
        }
      }
    `;
    const variables = { registryId };
    const response = await this.graphqlRequest<{ getRegistry: RegistryResponse | null }>(query, variables);
    return response?.getRegistry?.registry ?? null;
  }

  /**
   * Lists all registries with pagination.
   * @param limit - Maximum number of registries to return (default: 50, max: 100)
   * @param offset - Number of registries to skip (default: 0)
   * @returns Paginated response with registries, total count, and hasMore flag
   * @throws {ErrorAuthentication} If API key is required but not provided.
   * @throws {ApplicationError} If the GraphQL request fails.
   */
  async listRegisters(limit = 50, offset = 0): Promise<{ registries: Registry[]; total: number; hasMore: boolean }> {
    const query = `
      query ListRegisters($pagination: RegistryPagination!) {
        listRegisters(pagination: $pagination) {
          registries {
            registryId
            name
            sequenceId
            sequenceTemplate
            sequenceValue
            sequenceCounter
            createdAt
            modifiedAt
          }
          total
          hasMore
        }
      }
    `;
    const variables = { pagination: { limit, offset } };
    const response = await this.graphqlRequest<{
      listRegisters: { registries: Registry[]; total: number; hasMore: boolean };
    }>(query, variables);
    if (!response?.listRegisters) {
      throw new ApplicationError("Invalid response structure from listRegisters query", "INVALID_RESPONSE");
    }
    return response.listRegisters;
  }

  // --- Sequence Methods ---

  /**
   * Creates a new sequence.
   * @param input - Data for the new sequence.
   * @returns The created sequence.
   * @throws {ErrorAuthentication} If API key is required but not provided.
   * @throws {ApplicationError} If the GraphQL request fails.
   */
  async createSequence(input: CreateSequenceInput): Promise<Sequence> {
    const query = `
      mutation CreateSequence($input: CreateSequenceInput!) {
        createSequence(input: $input) {
          sequence {
            sequenceId
            name
            template
            start
            counter
            value
            createdAt
            modifiedAt
            history {
              counter
              value
              createdAt
            }
          }
          versionstamp
        }
      }
    `;
    const variables = { input };
    const response = await this.graphqlRequest<{ createSequence: SequenceResponse | null }>(query, variables);
    if (!response?.createSequence?.sequence) {
      throw new ApplicationError("Invalid response structure from createSequence mutation", "INVALID_RESPONSE");
    }
    return response.createSequence.sequence;
  }

  /**
   * Retrieves a specific sequence by its ID.
   * @param sequenceId - The UUID of the sequence to retrieve.
   * @returns The requested sequence or null if not found.
   * @throws {ErrorAuthentication} If API key is required but not provided.
   * @throws {ApplicationError} If the GraphQL request fails.
   */
  async getSequence(sequenceId: string): Promise<Sequence | null> {
    const query = `
      query GetSequence($sequenceId: UUID!) {
        getSequence(sequenceId: $sequenceId) {
          sequence {
            sequenceId
            name
            template
            start
            counter
            value
            createdAt
            modifiedAt
            history {
              counter
              value
              createdAt
            }
          }
          versionstamp
        }
      }
    `;
    const variables = { sequenceId };
    const response = await this.graphqlRequest<{ getSequence: SequenceResponse | null }>(query, variables);
    return response?.getSequence?.sequence ?? null;
  }

  /**
   * Lists all sequences with pagination.
   * @param limit - Maximum number of sequences to return (default: 50, max: 100)
   * @param offset - Number of sequences to skip (default: 0)
   * @returns Paginated response with sequences, total count, and hasMore flag
   * @throws {ErrorAuthentication} If API key is required but not provided.
   * @throws {ApplicationError} If the GraphQL request fails.
   */
  async listSequences(limit = 50, offset = 0): Promise<{ sequences: Sequence[]; total: number; hasMore: boolean }> {
    const query = `
      query ListSequences($pagination: SequencePaginationInput!) {
        listSequences(pagination: $pagination) {
          sequences {
            sequenceId
            name
            template
            start
            counter
            value
            createdAt
            modifiedAt
            history {
              counter
              value
              createdAt
            }
          }
          total
          hasMore
        }
      }
    `;
    const variables = { pagination: { limit, offset } };
    const response = await this.graphqlRequest<{
      listSequences: { sequences: Sequence[]; total: number; hasMore: boolean };
    }>(query, variables);
    if (!response?.listSequences) {
      throw new ApplicationError("Invalid response structure from listSequences query", "INVALID_RESPONSE");
    }
    return response.listSequences;
  }

  /**
   * Gets the next value for a sequence.
   * @param sequenceId - The UUID of the sequence.
   * @returns The updated sequence with the new value.
   * @throws {ErrorAuthentication} If API key is required but not provided.
   * @throws {ApplicationError} If the GraphQL request fails.
   */
  async nextSequence(sequenceId: string): Promise<Sequence> {
    const query = `
      mutation NextSequence($sequenceId: UUID!) {
        nextSequence(sequenceId: $sequenceId) {
          sequence {
            sequenceId
            name
            template
            start
            counter
            value
            createdAt
            modifiedAt
            history {
              counter
              value
              createdAt
            }
          }
          versionstamp
        }
      }
    `;
    const variables = { sequenceId };
    const response = await this.graphqlRequest<{ nextSequence: SequenceResponse | null }>(query, variables);
    if (!response?.nextSequence?.sequence) {
      throw new ApplicationError("Invalid response structure from nextSequence mutation", "INVALID_RESPONSE");
    }
    return response.nextSequence.sequence;
  }

  /**
   * Takes the next invoice number in the current month's series (#293).
   * The service picks the Europe/Warsaw month and its configured series;
   * the caller only names the document type.
   * @param type - The document type; only `VAT` is numbered this way.
   * @returns The sequence with the allocated number in `value`.
   * @throws {ErrorAuthentication} If API key is required but not provided.
   * @throws {ApplicationError} If the GraphQL request fails (REGISTRY_INACTIVE when the month was deactivated).
   */
  async nextInvoiceNumber(type: "VAT"): Promise<Sequence> {
    const query = `
      mutation NextInvoiceNumber($type: InvoiceNumberType!) {
        nextInvoiceNumber(type: $type) {
          sequence {
            sequenceId
            name
            template
            start
            counter
            value
            createdAt
            modifiedAt
          }
          versionstamp
        }
      }
    `;
    const response = await this.graphqlRequest<{ nextInvoiceNumber: SequenceResponse | null }>(query, { type });
    if (!response?.nextInvoiceNumber?.sequence) {
      throw new ApplicationError("Invalid response structure from nextInvoiceNumber mutation", "INVALID_RESPONSE");
    }
    return response.nextInvoiceNumber.sequence;
  }

  /**
   * Retrieves the history of a sequence.
   * @param sequenceId - The UUID of the sequence.
   * @returns The sequence history response or null if not found.
   * @throws {ErrorAuthentication} If API key is required but not provided.
   * @throws {ApplicationError} If the GraphQL request fails.
   */
  async getSequenceHistory(sequenceId: string): Promise<SequenceHistoryResponse | null> {
    const query = `
      query GetSequenceHistory($sequenceId: UUID!) {
        getSequenceHistory(sequenceId: $sequenceId) {
          sequenceId
          history {
            counter
            value
            createdAt
          }
          total
          hasMore
        }
      }
    `;
    const variables = { sequenceId };
    const response = await this.graphqlRequest<{ getSequenceHistory: SequenceHistoryResponse | null }>(
      query,
      variables,
    );
    return response?.getSequenceHistory ?? null;
  }

  /**
   * Updates a sequence's configuration.
   * @param input - The update input containing sequenceId and fields to update.
   * @returns The updated sequence.
   * @throws {ErrorAuthentication} If API key is required but not provided.
   * @throws {ApplicationError} If the GraphQL request fails.
   */
  async updateSequence(input: UpdateSequenceInput): Promise<Sequence> {
    const query = `
      mutation UpdateSequence($input: UpdateSequenceInput!) {
        updateSequence(input: $input) {
          sequence {
            sequenceId
            name
            template
            start
            counter
            value
            createdAt
            modifiedAt
          }
          versionstamp
        }
      }
    `;
    const variables = { input };
    const response = await this.graphqlRequest<{ updateSequence: SequenceResponse | null }>(query, variables);
    if (!response?.updateSequence?.sequence) {
      throw new ApplicationError("Invalid response structure from updateSequence mutation", "INVALID_RESPONSE");
    }
    return response.updateSequence.sequence;
  }

  /**
   * Updates a registry's name.
   * @param input - The update input containing registryId, version, and optional name.
   * @returns The updated registry with full sequence details.
   * @throws {ErrorAuthentication} If API key is required but not provided.
   * @throws {ApplicationError} If the GraphQL request fails.
   */
  async updateRegistry(input: UpdateRegistryInput): Promise<RegistryWithFullSequence> {
    const query = `
      mutation UpdateRegistry($input: UpdateRegistryInput!) {
        updateRegistry(input: $input) {
          registry {
            registryId
            name
            sequenceId
            createdAt
            modifiedAt
            sequence {
              sequenceId
              name
              start
              template
              counter
              value
              createdAt
              modifiedAt
            }
          }
          versionstamp
        }
      }
    `;
    const variables = { input };
    const response = await this.graphqlRequest<{ updateRegistry: RegistryResponse | null }>(query, variables);
    if (!response?.updateRegistry?.registry) {
      throw new ApplicationError("Invalid response structure from updateRegistry mutation", "INVALID_RESPONSE");
    }
    return response.updateRegistry.registry;
  }

  /**
   * Deactivates a registry, preventing new invoice creation.
   * @param registryId - The UUID of the registry to deactivate.
   * @param reason - Optional reason for deactivation.
   * @returns The deactivated registry with full sequence details.
   * @throws {ErrorAuthentication} If API key is required but not provided.
   * @throws {ApplicationError} If the GraphQL request fails.
   */
  /**
   * Deactivates an invoice (soft delete).
   *
   * There is no hard-delete mutation -- Polish tax law forbids gaps in issued
   * numbering -- so deactivation is the disposal boundary for test invoices.
   */
  async deactivateInvoice(invoiceId: string, registryId: string, reason?: string): Promise<Invoice> {
    const query = `
      mutation DeactivateInvoice($invoiceId: UUID!, $registryId: UUID!, $reason: String) {
        deactivateInvoice(invoiceId: $invoiceId, registryId: $registryId, reason: $reason) {
          invoice {
            invoiceId
          }
        }
      }
    `;
    const variables = { invoiceId, registryId, reason };
    const response = await this.graphqlRequest<{ deactivateInvoice: { invoice: Invoice | null } | null }>(
      query,
      variables,
    );
    if (!response?.deactivateInvoice?.invoice) {
      throw new ApplicationError("Invalid response structure from deactivateInvoice mutation", "INVALID_RESPONSE");
    }
    return response.deactivateInvoice.invoice;
  }

  async deactivateRegistry(registryId: string, reason?: string): Promise<RegistryWithFullSequence> {
    const query = `
      mutation DeactivateRegistry($registryId: UUID!, $reason: String) {
        deactivateRegistry(registryId: $registryId, reason: $reason) {
          registry {
            registryId
            name
            sequenceId
            createdAt
            modifiedAt
            sequence {
              sequenceId
              name
              start
              template
              counter
              value
              createdAt
              modifiedAt
            }
          }
          versionstamp
        }
      }
    `;
    const variables = { registryId, reason };
    const response = await this.graphqlRequest<{ deactivateRegistry: RegistryResponse | null }>(query, variables);
    if (!response?.deactivateRegistry?.registry) {
      throw new ApplicationError("Invalid response structure from deactivateRegistry mutation", "INVALID_RESPONSE");
    }
    return response.deactivateRegistry.registry;
  }

  /**
   * Reactivates a deactivated registry.
   * @param registryId - The UUID of the registry to reactivate.
   * @returns The reactivated registry with full sequence details.
   * @throws {ErrorAuthentication} If API key is required but not provided.
   * @throws {ApplicationError} If the GraphQL request fails.
   */
  async reactivateRegistry(registryId: string): Promise<RegistryWithFullSequence> {
    const query = `
      mutation ReactivateRegistry($registryId: UUID!) {
        reactivateRegistry(registryId: $registryId) {
          registry {
            registryId
            name
            sequenceId
            createdAt
            modifiedAt
            sequence {
              sequenceId
              name
              start
              template
              counter
              value
              createdAt
              modifiedAt
            }
          }
          versionstamp
        }
      }
    `;
    const variables = { registryId };
    const response = await this.graphqlRequest<{ reactivateRegistry: RegistryResponse | null }>(query, variables);
    if (!response?.reactivateRegistry?.registry) {
      throw new ApplicationError("Invalid response structure from reactivateRegistry mutation", "INVALID_RESPONSE");
    }
    return response.reactivateRegistry.registry;
  }

  /**
   * Updates an existing invoice.
   * @param input - The update input containing invoiceId, registryId, version, and fields to update.
   * @returns The updated invoice.
   * @throws {ErrorAuthentication} If API key is required but not provided.
   * @throws {ApplicationError} If the GraphQL request fails.
   */
  async updateInvoice(input: UpdateInvoiceInput): Promise<Invoice> {
    const query = `
      mutation UpdateInvoice($input: UpdateInvoiceInput!) {
        updateInvoice(input: $input) {
          invoice {
            invoiceId
            registryId
            serialNumber
            seller {
              name
              taxId
              address { street city zip country countryCode }
              iban
            }
            buyer {
              name
              taxId
              address { street city zip country countryCode }
            }
            items {
              name
              description
              quantity
              measureUnit
              unitPrice { rate netto brutto tax }
              totalPrice { rate netto brutto tax }
              taxCode
              reference
            }
            total { amount currency }
            groupedTax { rate netto brutto tax }
            createdAt
            modifiedAt
            due
            issued
            sold
            paid
          }
          versionstamp
        }
      }
    `;
    const preparedInput = this.prepareUpdateInvoiceInput(input);
    const variables = { input: preparedInput };
    const response = await this.graphqlRequest<{ updateInvoice: InvoiceResponse | null }>(query, variables);
    if (!response?.updateInvoice?.invoice) {
      throw new ApplicationError("Invalid response structure from updateInvoice mutation", "INVALID_RESPONSE");
    }
    return response.updateInvoice.invoice;
  }

  /**
   * Marks an invoice as paid.
   * @param invoiceId - The UUID of the invoice.
   * @param registryId - The UUID of the registry the invoice belongs to.
   * @param paidDate - The payment date in YYYY-MM-DD format.
   * @returns The updated invoice with payment date.
   * @throws {ErrorAuthentication} If API key is required but not provided.
   * @throws {ApplicationError} If the GraphQL request fails.
   */
  async markInvoicePaid(invoiceId: string, registryId: string, paidDate: string): Promise<Invoice> {
    const query = `
      mutation MarkInvoicePaid($invoiceId: UUID!, $registryId: UUID!, $paidDate: Date!) {
        markInvoicePaid(invoiceId: $invoiceId, registryId: $registryId, paidDate: $paidDate) {
          invoice {
            invoiceId
            registryId
            serialNumber
            seller {
              name
              taxId
              address { street city zip country countryCode }
              iban
            }
            buyer {
              name
              taxId
              address { street city zip country countryCode }
            }
            items {
              name
              description
              quantity
              measureUnit
              unitPrice { rate netto brutto tax }
              totalPrice { rate netto brutto tax }
              taxCode
              reference
            }
            total { amount currency }
            groupedTax { rate netto brutto tax }
            createdAt
            modifiedAt
            due
            issued
            sold
            paid
          }
          versionstamp
        }
      }
    `;
    const variables = { invoiceId, registryId, paidDate };
    const response = await this.graphqlRequest<{ markInvoicePaid: InvoiceResponse | null }>(query, variables);
    if (!response?.markInvoicePaid?.invoice) {
      throw new ApplicationError("Invalid response structure from markInvoicePaid mutation", "INVALID_RESPONSE");
    }
    return response.markInvoicePaid.invoice;
  }

  // --- Draft Methods ---

  /** Common GraphQL fragment for draft fields */
  private static readonly DRAFT_FIELDS = `
    draftId
    draftType
    status
    content {
      seller {
        name
        taxId
        address {
          street
          city
          zip
          country
          countryCode
        }
        iban
      }
      buyer {
        name
        taxId
        address {
          street
          city
          zip
          country
          countryCode
        }
      }
      items {
        name
        description
        quantity
        unitPrice {
          rate
          netto
          brutto
          tax
        }
        totalPrice {
          rate
          netto
          brutto
          tax
        }
        measureUnit
        taxCode
        reference
      }
      total {
        amount
        currency
      }
      groupedTax {
        rate
        netto
        brutto
        tax
      }
      issued
      sold
      due
      paid
    }
    originalInvoiceId
    originalRegistryId
    targetRegistryId
    issueType
    issuedInvoiceId
    issuedInvoice {
      invoiceId
      serialNumber
    }
    rejectionReason
    approvalRequestedBy
    approvedBy
    createdBy
    createdByType
    version
    createdAt
    updatedAt
    approvalRequestedAt
    issuedAt
    rejectedAt
    discardedAt
    history {
      id
      fromStatus
      toStatus
      actorId
      actorType
      action
      changes
      reason
      timestamp
    }
  `;

  /**
   * Creates a new invoice draft for iterative refinement before approval.
   * @param input - The draft content and optional target registry.
   * @param actor - The actor creating the draft (id and type).
   * @returns The created draft.
   * @throws {ErrorAuthentication} If API key is required but not provided.
   * @throws {ApplicationError} If the GraphQL request fails.
   */
  async createInvoiceDraft(
    input: {
      content: Record<string, unknown>;
      targetRegistryId?: string;
    },
    actor: { id: string; type: "AGENT" | "USER" | "SYSTEM" },
  ): Promise<InvoiceDraft> {
    const query = `
      mutation CreateInvoiceDraft($input: CreateInvoiceDraftInput!) {
        createInvoiceDraft(input: $input) {
          ${InvoicesClient.DRAFT_FIELDS}
        }
      }
    `;
    const normalizedContent = this.normalizeDraftContentDates(input.content);
    const variables = {
      input: {
        content: normalizedContent,
        targetRegistryId: input.targetRegistryId,
        actor,
      },
    };
    const response = await this.graphqlRequest<{ createInvoiceDraft: InvoiceDraft }>(query, variables);
    if (!response?.createInvoiceDraft) {
      throw new ApplicationError("Invalid response structure from createInvoiceDraft mutation", "INVALID_RESPONSE");
    }
    return response.createInvoiceDraft;
  }

  /**
   * Creates a correction draft for an existing invoice.
   * @param input - The original invoice ID and draft content.
   * @param actor - The actor creating the draft.
   * @returns The created correction draft.
   * @throws {ErrorAuthentication} If API key is required but not provided.
   * @throws {ApplicationError} If the GraphQL request fails.
   */
  async createCorrectionDraft(
    input: {
      originalInvoiceId: string;
      content: Record<string, unknown>;
      targetRegistryId?: string;
    },
    actor: { id: string; type: "AGENT" | "USER" | "SYSTEM" },
  ): Promise<InvoiceDraft> {
    const query = `
      mutation CreateCorrectionDraft($input: CreateCorrectionDraftInput!) {
        createCorrectionDraft(input: $input) {
          ${InvoicesClient.DRAFT_FIELDS}
        }
      }
    `;
    const variables = {
      input: {
        originalInvoiceId: input.originalInvoiceId,
        content: this.normalizeDraftContentDates(input.content),
        targetRegistryId: input.targetRegistryId,
        actor,
      },
    };
    const response = await this.graphqlRequest<{ createCorrectionDraft: InvoiceDraft }>(query, variables);
    if (!response?.createCorrectionDraft) {
      throw new ApplicationError("Invalid response structure from createCorrectionDraft mutation", "INVALID_RESPONSE");
    }
    return response.createCorrectionDraft;
  }

  /**
   * Retrieves a draft by ID.
   * @param draftId - The UUID of the draft.
   * @returns The draft or null if not found.
   * @throws {ErrorAuthentication} If API key is required but not provided.
   * @throws {ApplicationError} If the GraphQL request fails.
   */
  async getDraft(draftId: string): Promise<InvoiceDraft | null> {
    const query = `
      query GetDraft($draftId: UUID!) {
        getDraft(draftId: $draftId) {
          ${InvoicesClient.DRAFT_FIELDS}
        }
      }
    `;
    const variables = { draftId };
    const response = await this.graphqlRequest<{ getDraft: InvoiceDraft | null }>(query, variables);
    return response?.getDraft ?? null;
  }

  /**
   * Lists drafts with optional filtering and pagination.
   * @param options - Filter and pagination options.
   * @returns Paginated list of drafts.
   * @throws {ErrorAuthentication} If API key is required but not provided.
   * @throws {ApplicationError} If the GraphQL request fails.
   */
  async listDrafts(
    options: {
      filter?: {
        status?: string;
        draftType?: string;
        createdBy?: string;
        createdByType?: string;
        originalInvoiceId?: string;
        targetRegistryId?: string;
      };
      pagination?: { limit?: number; offset?: number };
    } = {},
  ): Promise<DraftListResponse> {
    const query = `
      query ListDrafts($filter: DraftFilterInput, $pagination: DraftPaginationInput!) {
        listDrafts(filter: $filter, pagination: $pagination) {
          drafts {
            ${InvoicesClient.DRAFT_FIELDS}
          }
          total
          hasMore
        }
      }
    `;
    const variables = {
      filter: options.filter || null,
      pagination: options.pagination || { limit: 50, offset: 0 },
    };
    const response = await this.graphqlRequest<{ listDrafts: DraftListResponse }>(query, variables);
    if (!response?.listDrafts) {
      throw new ApplicationError("Invalid response structure from listDrafts query", "INVALID_RESPONSE");
    }
    return response.listDrafts;
  }

  /**
   * Updates a draft's content.
   * @param draftId - The UUID of the draft.
   * @param input - The updated content and version.
   * @param actor - The actor making the update.
   * @returns The updated draft.
   * @throws {ErrorAuthentication} If API key is required but not provided.
   * @throws {ApplicationError} If the GraphQL request fails.
   */
  async updateDraft(
    draftId: string,
    input: { content?: Record<string, unknown>; targetRegistryId?: string; version: number },
    actor: { id: string; type: "AGENT" | "USER" | "SYSTEM" },
  ): Promise<InvoiceDraft> {
    const query = `
      mutation UpdateDraft($draftId: UUID!, $input: UpdateDraftInput!) {
        updateDraft(draftId: $draftId, input: $input) {
          ${InvoicesClient.DRAFT_FIELDS}
        }
      }
    `;
    const variables = {
      draftId,
      input: {
        content: input.content ? this.normalizeDraftContentDates(input.content) : undefined,
        targetRegistryId: input.targetRegistryId,
        version: input.version,
        actor,
      },
    };
    const response = await this.graphqlRequest<{ updateDraft: InvoiceDraft }>(query, variables);
    if (!response?.updateDraft) {
      throw new ApplicationError("Invalid response structure from updateDraft mutation", "INVALID_RESPONSE");
    }
    return response.updateDraft;
  }

  /**
   * Validates a draft before requesting approval.
   * @param draftId - The UUID of the draft.
   * @returns Validation result with errors, warnings, and suggestions.
   * @throws {ErrorAuthentication} If API key is required but not provided.
   * @throws {ApplicationError} If the GraphQL request fails.
   */
  async validateDraft(draftId: string): Promise<DraftValidationResult> {
    const query = `
      query ValidateDraft($draftId: UUID!) {
        validateDraft(draftId: $draftId) {
          valid
          canRequestApproval
          errors { code severity field message expected actual }
          warnings { code severity field message expected actual }
          calculations { field expected actual valid tolerance }
          suggestions { field issue currentValue suggestedValue autoFixable }
          errorCount
          warningCount
        }
      }
    `;
    const variables = { draftId };
    const response = await this.graphqlRequest<{ validateDraft: DraftValidationResult }>(query, variables);
    if (!response?.validateDraft) {
      throw new ApplicationError("Invalid response structure from validateDraft query", "INVALID_RESPONSE");
    }
    return response.validateDraft;
  }

  /**
   * Requests approval for a draft.
   * @param draftId - The UUID of the draft.
   * @param issueType - The type of invoice to issue (PROFORMA or VAT).
   * @param actor - The actor requesting approval.
   * @returns The draft in pending_approval status.
   * @throws {ErrorAuthentication} If API key is required but not provided.
   * @throws {ApplicationError} If the GraphQL request fails.
   */
  async requestApproval(
    draftId: string,
    issueType: "PROFORMA" | "VAT",
    actor: { id: string; type: "AGENT" | "USER" | "SYSTEM" },
  ): Promise<InvoiceDraft> {
    const query = `
      mutation RequestApproval($draftId: UUID!, $issueType: IssueType!, $actor: ActorInput!) {
        requestApproval(draftId: $draftId, issueType: $issueType, actor: $actor) {
          ${InvoicesClient.DRAFT_FIELDS}
        }
      }
    `;
    const variables = { draftId, issueType, actor };
    const response = await this.graphqlRequest<{ requestApproval: InvoiceDraft }>(query, variables);
    if (!response?.requestApproval) {
      throw new ApplicationError("Invalid response structure from requestApproval mutation", "INVALID_RESPONSE");
    }
    return response.requestApproval;
  }

  /**
   * Approves a draft and issues the invoice.
   * @param draftId - The UUID of the draft.
   * @param actor - The actor approving the draft.
   * @returns The approval result with issued invoice.
   * @throws {ErrorAuthentication} If API key is required but not provided.
   * @throws {ApplicationError} If the GraphQL request fails.
   */
  async approveDraft(
    draftId: string,
    actor: { id: string; type: "AGENT" | "USER" | "SYSTEM" },
  ): Promise<ApprovalResult> {
    const query = `
      mutation ApproveDraft($draftId: UUID!, $actor: ActorInput!) {
        approveDraft(draftId: $draftId, actor: $actor) {
          draft {
            ${InvoicesClient.DRAFT_FIELDS}
          }
          issuedInvoice {
            invoiceId
            registryId
            serialNumber
            total { amount currency }
          }
          registryId
          serialNumber
        }
      }
    `;
    const variables = { draftId, actor };
    const response = await this.graphqlRequest<{ approveDraft: ApprovalResult }>(query, variables);
    if (!response?.approveDraft) {
      throw new ApplicationError("Invalid response structure from approveDraft mutation", "INVALID_RESPONSE");
    }
    return response.approveDraft;
  }

  /**
   * Rejects a draft with a reason.
   * @param draftId - The UUID of the draft.
   * @param reason - The rejection reason.
   * @param actor - The actor rejecting the draft.
   * @returns The rejected draft.
   * @throws {ErrorAuthentication} If API key is required but not provided.
   * @throws {ApplicationError} If the GraphQL request fails.
   */
  async rejectDraft(
    draftId: string,
    reason: string,
    actor: { id: string; type: "AGENT" | "USER" | "SYSTEM" },
  ): Promise<InvoiceDraft> {
    const query = `
      mutation RejectDraft($draftId: UUID!, $reason: String!, $actor: ActorInput!) {
        rejectDraft(draftId: $draftId, reason: $reason, actor: $actor) {
          ${InvoicesClient.DRAFT_FIELDS}
        }
      }
    `;
    const variables = { draftId, reason, actor };
    const response = await this.graphqlRequest<{ rejectDraft: InvoiceDraft }>(query, variables);
    if (!response?.rejectDraft) {
      throw new ApplicationError("Invalid response structure from rejectDraft mutation", "INVALID_RESPONSE");
    }
    return response.rejectDraft;
  }

  /**
   * Discards a draft permanently.
   * @param draftId - The UUID of the draft.
   * @param actor - The actor discarding the draft.
   * @returns The discarded draft.
   * @throws {ErrorAuthentication} If API key is required but not provided.
   * @throws {ApplicationError} If the GraphQL request fails.
   */
  async discardDraft(draftId: string, actor: { id: string; type: "AGENT" | "USER" | "SYSTEM" }): Promise<InvoiceDraft> {
    const query = `
      mutation DiscardDraft($draftId: UUID!, $actor: ActorInput!) {
        discardDraft(draftId: $draftId, actor: $actor) {
          ${InvoicesClient.DRAFT_FIELDS}
        }
      }
    `;
    const variables = { draftId, actor };
    const response = await this.graphqlRequest<{ discardDraft: InvoiceDraft }>(query, variables);
    if (!response?.discardDraft) {
      throw new ApplicationError("Invalid response structure from discardDraft mutation", "INVALID_RESPONSE");
    }
    return response.discardDraft;
  }

  // --- Linking Methods ---

  /**
   * Links a Stripe payment ID to an invoice.
   * @param invoiceId - The UUID of the invoice.
   * @param registryId - The UUID of the registry the invoice belongs to.
   * @param paymentId - The Stripe payment ID (e.g., pi_xxx or cs_xxx).
   * @returns The result with updated invoice and link timestamp.
   * @throws {ErrorAuthentication} If API key is required but not provided.
   * @throws {ApplicationError} If the GraphQL request fails.
   */
  async linkPayment(invoiceId: string, registryId: string, paymentId: string): Promise<LinkPaymentResult> {
    const query = `
      mutation LinkPayment($input: LinkPaymentInput!) {
        linkPayment(input: $input) {
          invoice {
            invoiceId
            registryId
            serialNumber
            total { amount currency }
            paid
          }
          success
          linkedAt
        }
      }
    `;
    const variables = { input: { invoiceId, registryId, paymentId } };
    const response = await this.graphqlRequest<{ linkPayment: LinkPaymentResult }>(query, variables);
    if (!response?.linkPayment) {
      throw new ApplicationError("Invalid response structure from linkPayment mutation", "INVALID_RESPONSE");
    }
    return response.linkPayment;
  }

  /**
   * Links a Stripe subscription ID to an invoice.
   * @param invoiceId - The UUID of the invoice.
   * @param registryId - The UUID of the registry the invoice belongs to.
   * @param subscriptionId - The Stripe subscription ID (e.g., sub_xxx).
   * @returns The result with updated invoice and link timestamp.
   * @throws {ErrorAuthentication} If API key is required but not provided.
   * @throws {ApplicationError} If the GraphQL request fails.
   */
  async linkSubscription(
    invoiceId: string,
    registryId: string,
    subscriptionId: string,
  ): Promise<LinkSubscriptionResult> {
    const query = `
      mutation LinkSubscription($input: LinkSubscriptionInput!) {
        linkSubscription(input: $input) {
          invoice {
            invoiceId
            registryId
            serialNumber
            total { amount currency }
          }
          success
          linkedAt
        }
      }
    `;
    const variables = { input: { invoiceId, registryId, subscriptionId } };
    const response = await this.graphqlRequest<{ linkSubscription: LinkSubscriptionResult }>(query, variables);
    if (!response?.linkSubscription) {
      throw new ApplicationError("Invalid response structure from linkSubscription mutation", "INVALID_RESPONSE");
    }
    return response.linkSubscription;
  }

  /**
   * Converts an approved proforma invoice to a VAT invoice.
   * @param proformaId - The UUID of the proforma invoice.
   * @param proformaRegistryId - The UUID of the registry the proforma belongs to.
   * @param vatRegistryId - The UUID of the registry where the VAT invoice should be created.
   * @param paymentId - Optional Stripe payment ID to link to the new VAT invoice.
   * @returns The result with new VAT invoice and original proforma.
   * @throws {ErrorAuthentication} If API key is required but not provided.
   * @throws {ApplicationError} If the GraphQL request fails.
   */
  async convertProformaToVat(
    proformaId: string,
    proformaRegistryId: string,
    vatRegistryId: string,
    paymentId?: string,
  ): Promise<ConvertProformaResult> {
    const query = `
      mutation ConvertProformaToVat($input: ConvertProformaInput!) {
        convertProformaToVat(input: $input) {
          vatInvoice {
            invoiceId
            registryId
            serialNumber
            total { amount currency }
            paid
          }
          proforma {
            invoiceId
            registryId
            serialNumber
          }
          success
          convertedAt
        }
      }
    `;
    const variables = { input: { proformaId, proformaRegistryId, vatRegistryId, paymentId } };
    const response = await this.graphqlRequest<{ convertProformaToVat: ConvertProformaResult }>(query, variables);
    if (!response?.convertProformaToVat) {
      throw new ApplicationError("Invalid response structure from convertProformaToVat mutation", "INVALID_RESPONSE");
    }
    return response.convertProformaToVat;
  }

  /**
   * Finds invoices linked to a payment or subscription.
   * @param options - Filter options with paymentId and/or subscriptionId.
   * @returns Paginated list of linked invoices.
   * @throws {ErrorAuthentication} If API key is required but not provided.
   * @throws {ApplicationError} If the GraphQL request fails.
   */
  async getLinkedInvoices(options: {
    paymentId?: string;
    subscriptionId?: string;
    limit?: number;
    offset?: number;
  }): Promise<LinkedInvoicesResult> {
    const query = `
      query GetLinkedInvoices($input: GetLinkedInvoicesInput!) {
        getLinkedInvoices(input: $input) {
          invoices {
            invoiceId
            registryId
            serialNumber
            seller {
              name
              taxId
              address { street city zip country countryCode }
              iban
            }
            buyer {
              name
              taxId
              address { street city zip country countryCode }
            }
            total { amount currency }
            createdAt
            modifiedAt
            due
            issued
            sold
            paid
          }
          total
        }
      }
    `;
    const variables = { input: options };
    const response = await this.graphqlRequest<{ getLinkedInvoices: LinkedInvoicesResult }>(query, variables);
    if (!response?.getLinkedInvoices) {
      throw new ApplicationError("Invalid response structure from getLinkedInvoices query", "INVALID_RESPONSE");
    }
    return response.getLinkedInvoices;
  }
}

// --- Draft Types ---

/**
 * Invoice draft type returned by the API.
 */
export interface InvoiceDraft {
  draftId: string;
  draftType: "INVOICE" | "CORRECTION";
  status: "DRAFT_CREATED" | "DRAFT_UPDATED" | "PENDING_APPROVAL" | "ISSUED" | "REJECTED" | "DISCARDED";
  content: Record<string, unknown>;
  originalInvoiceId?: string | null;
  originalRegistryId?: string | null;
  targetRegistryId?: string | null;
  issueType?: "PROFORMA" | "VAT" | null;
  issuedInvoiceId?: string | null;
  issuedInvoice?: {
    invoiceId: string;
    serialNumber: string;
  } | null;
  rejectionReason?: string | null;
  approvalRequestedBy?: string | null;
  approvedBy?: string | null;
  createdBy: string;
  createdByType: "AGENT" | "USER" | "SYSTEM";
  version: number;
  createdAt: string;
  updatedAt: string;
  approvalRequestedAt?: string | null;
  issuedAt?: string | null;
  rejectedAt?: string | null;
  discardedAt?: string | null;
  history: DraftHistoryEntry[];
}

/**
 * Draft history entry.
 */
export interface DraftHistoryEntry {
  id: number;
  fromStatus?: string | null;
  toStatus: string;
  actorId: string;
  actorType: string;
  action: string;
  changes?: string | null;
  reason?: string | null;
  timestamp: string;
}

/**
 * Draft list response.
 */
export interface DraftListResponse {
  drafts: InvoiceDraft[];
  total: number;
  hasMore: boolean;
}

/**
 * Draft validation result.
 */
export interface DraftValidationResult {
  valid: boolean;
  canRequestApproval: boolean;
  errors: ValidationIssue[];
  warnings: ValidationIssue[];
  calculations: CalculationCheck[];
  suggestions: AutoFixSuggestion[];
  errorCount: number;
  warningCount: number;
}

/**
 * Validation issue (error or warning).
 */
export interface ValidationIssue {
  code: string;
  severity: "ERROR" | "WARNING";
  field: string;
  message: string;
  expected?: string | null;
  actual?: string | null;
}

/**
 * Calculation check result.
 */
export interface CalculationCheck {
  field: string;
  expected: number;
  actual: number;
  valid: boolean;
  tolerance: number;
}

/**
 * Auto-fix suggestion.
 */
export interface AutoFixSuggestion {
  field: string;
  issue: string;
  currentValue: number;
  suggestedValue: number;
  autoFixable: boolean;
}

/**
 * Approval result with issued invoice.
 */
export interface ApprovalResult {
  draft: InvoiceDraft;
  issuedInvoice: {
    invoiceId: string;
    registryId: string;
    serialNumber: string;
    total: { amount: number; currency: string };
  };
  registryId: string;
  serialNumber: string;
}

// --- Linking Types ---

/**
 * Result of linking a payment to an invoice.
 */
export interface LinkPaymentResult {
  invoice: {
    invoiceId: string;
    registryId: string;
    serialNumber: string;
    total: { amount: number; currency: string };
    paid: string | null;
  };
  success: boolean;
  linkedAt: string;
}

/**
 * Result of linking a subscription to an invoice.
 */
export interface LinkSubscriptionResult {
  invoice: {
    invoiceId: string;
    registryId: string;
    serialNumber: string;
    total: { amount: number; currency: string };
  };
  success: boolean;
  linkedAt: string;
}

/**
 * Result of converting proforma to VAT invoice.
 */
export interface ConvertProformaResult {
  vatInvoice: {
    invoiceId: string;
    registryId: string;
    serialNumber: string;
    total: { amount: number; currency: string };
    paid: string | null;
  };
  proforma: {
    invoiceId: string;
    registryId: string;
    serialNumber: string;
  };
  success: boolean;
  convertedAt: string;
}

/**
 * Result of querying linked invoices.
 */
export interface LinkedInvoicesResult {
  invoices: Array<{
    invoiceId: string;
    registryId: string;
    serialNumber: string;
    seller: {
      name: string;
      taxId: string;
      address: { street: string; city: string; zip: string; country: string; countryCode: string | null };
      iban: string | null;
    };
    buyer: {
      name: string;
      taxId: string;
      address: { street: string; city: string; zip: string; country: string; countryCode: string | null };
    };
    total: { amount: number; currency: string };
    createdAt: string;
    modifiedAt: string;
    due: string;
    issued: string;
    sold: string;
    paid: string | null;
  }>;
  total: number;
}
